// ─────────────────────────────────────────────────────────────────────────────
// Wallet – Shared API / Domain Types
// Consumed by: @repo/store (walletSlice, walletThunk) and apps/main wallet feature
// Consumed by: @repo/store (walletSlice, walletThunk) and apps/staff wallet feature
// ─────────────────────────────────────────────────────────────────────────────

import type { ApiResponse } from "./apiResponse";

export type WalletCreditStatus = "ACTIVE" | "PARTIALLY_CONSUMED" | "CONSUMED" | "EXPIRED";

// Known values + open string for forward compatibility with new credit types
export type WalletCreditType = "REFUND" | "COMPENSATION" | (string & {});

export type WalletAsyncStatus = "idle" | "loading" | "success" | "error";

export interface WalletCreditJourney {
  flight: string | null;
  source: string | null;
  destination: string | null;
}

export interface WalletCredit {
  createdAt: string;
  voId: string;
  expireAt: string;
  status: WalletCreditStatus;
  amount: number;
  sourcePnr: string;
  eventId: string;
  personKey: string;
  voucherIssuanceKey: string;
  currencyCode: string;
  configurationCode: string;
  voucherKey: string;
  creditType: WalletCreditType;
  originalAmountUsd: number;
  remainingAmountUsd: number;
  consumedAmountUsd: number;
  journey?: WalletCreditJourney;
}

export interface WalletDetailsData {
  primaryContactEmail: string;
  customerNumber: string;
  currency: string;
  balance: number;
  credits: WalletCredit[];
}

export type WalletDetailsResponse = ApiResponse<WalletDetailsData>;

// ─── Wallet Summary ───────────────────────────────────────────────────────────

export interface WalletSummaryNearestExpiration {
  expireAt: string;
  availableBalance: number;
}

export interface WalletSummaryData {
  totalBalance: number;
  currencyCode: string;
  redeemableRefundBalance: number;
  nearestExpiration?: WalletSummaryNearestExpiration;
}

export type WalletSummaryResponse = ApiResponse<WalletSummaryData>;

// ─── Shared business constants ────────────────────────────────────────────────

// Vouchers with these statuses are still spendable (contribute to balance).
export const ACTIVE_CREDIT_STATUSES: ReadonlySet<WalletCreditStatus> = new Set<WalletCreditStatus>([
  "ACTIVE",
  "PARTIALLY_CONSUMED",
]);

// Vouchers visible in the UI — includes expired (shown in red).
export const VISIBLE_CREDIT_STATUSES: ReadonlySet<WalletCreditStatus> = new Set<WalletCreditStatus>(
  ["ACTIVE", "PARTIALLY_CONSUMED", "EXPIRED"],
);

export function isActiveCredit(c: WalletCredit): boolean {
  return ACTIVE_CREDIT_STATUSES.has(c.status);
}

// Returns the Tailwind text-color class for expiry date display:
// red → expired, yellow → expiring soon, light blue → normal.
export function getCreditExpiryColorClass(
  status: WalletCreditStatus,
  isExpiringSoon: boolean,
): string {
  if (status === "EXPIRED") return "text-red-500";
  if (isExpiringSoon) return "text-[#FFA400]";
  return "text-[#00AEC7]";
}

// Default per story AC4. Replace with CMS value when available.
export const DEFAULT_EXPIRING_SOON_DAYS = 60;

export function isCreditExpiringSoon(expireAt: string): boolean {
  const ms = new Date(expireAt).getTime() - Date.now();
  return ms > 0 && ms <= DEFAULT_EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;
}

// ─── Shared presentation types ────────────────────────────────────────────────

export interface WalletBalance {
  availableToPurchase: number;
  availableForWithdrawal: number;
  currency: string;
  closestExpiration?: string;
  expiringAmount?: number;
}

// ─── Last Movements – raw API response shape ─────────────────────────────────

export interface WalletMovementJourney {
  source: string | null;
  destination: string | null;
  bookedDate: string;
  flight: string | null;
  recordLocator: string | null;
  creditType: string | null;
}

export interface WalletMovementRecord {
  type: "debit" | "credit" | "transfer";
  recordLocator: string | null;
  amount: number;
  currency: string;
  date: string;
  journey?: WalletMovementJourney;
  voucherKey?: string;
  notes?: string;
}

// Kept here so @repo/store can import without depending on the API function.
export type WalletHistoryResponse = ApiResponse<WalletMovementRecord[]>;

const isValidStr = (v: string | null | undefined): v is string =>
  Boolean(v) && !v!.includes("undefined");

export function formatCreditFlightInfo(journey?: WalletCreditJourney): string {
  const flight = isValidStr(journey?.flight) ? (journey?.flight ?? null) : null;
  const source = isValidStr(journey?.source) ? (journey?.source ?? null) : null;
  const destination = isValidStr(journey?.destination) ? (journey?.destination ?? null) : null;

  if (!flight && !source && !destination) return "";
  const parts = [flight, source && destination ? `${source} to ${destination}` : null]
    .filter(Boolean)
    .join(" - ");
  return parts ? `Flight No.: ${parts}` : "";
}

// Transformed entry — returned by toLastMovements(), satisfies WalletMovementItem for display.
export interface WalletMovementEntry {
  id: string;
  type: "refund" | "compensation" | "refund_in_progress" | "purchase" | "transfer";
  description: string;
  amount: number;
  currency: string;
  date: string;
  addedAt: string;
  bookingCode: string;
  flightInfo?: string;
}

// ─── Reserve to withdraw – selectable reservation ─────────────────────────────
// Only REFUND credits are withdrawable to a bank account; compensations are
// redeemable for future purchases only (see WalletBalanceCard tooltip copy).

export interface WalletWithdrawableReservation {
  id: string;
  bookingCode: string;
  flightInfo: string;
  amount: number;
  currency: string;
  expireAt: string;
  isExpiringSoon: boolean;
}

export function toWithdrawableReservations(
  credits: WalletCredit[],
): WalletWithdrawableReservation[] {
  return credits
    .filter((c) => c.creditType.toUpperCase() === "REFUND" && ACTIVE_CREDIT_STATUSES.has(c.status))
    .map((c) => ({
      id: c.voId,
      bookingCode: c.sourcePnr,
      flightInfo: formatCreditFlightInfo(c.journey),
      amount: c.remainingAmountUsd,
      currency: c.currencyCode,
      expireAt: c.expireAt,
      isExpiringSoon: isCreditExpiringSoon(c.expireAt),
    }));
}

// ─── Refund redirect token ──────────────────────────────────────────────────
// Requested once the user picks a reservation in the "Reserve to withdraw"
// modal; the returned redirectUrl sends them to the external refund flow.

export interface WalletRefundRedirectTokenRequest {
  pnr: string;
  primaryContactEmail: string;
}

export interface WalletRefundRedirectTokenData {
  token: string;
  redirectUrl: string;
}

export type WalletRefundRedirectTokenResponse = ApiResponse<WalletRefundRedirectTokenData>;
